import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import IndicationComparison from '../IndicationComparison';
import { Study, IndicationItem, IndicationApiForComparison } from '../types';
import styles from './styles';

/* eslint-disable no-unused-vars */
interface IndicationComparisonBlockProps {
  prevItem: IndicationItem | null | undefined;
  currItem: IndicationItem | null | undefined;
  index: number;
  indicationId: number;
  // eslint-disable-next-line react/require-default-props
  indicationApi?: IndicationApiForComparison;
  nctIdAttributeId: number | null;
  identifierAttributeId: number | null;
  editingStudy: {
    indicationId: number;
    studyId: number | string;
    studyData: Study;
  } | null;
  onEditStudy: (indicationId: number, study: Study) => void;
  onSaveStudy: () => void;
  onUpdateStudyField: (indicationId: number, key: string, value: string | number | boolean) => void;
  onUpdateStudyAttribute: (
    indicationId: number,
    attrName: string,
    attrId: number,
    value: string
  ) => void;
  onCopyIndication: (prevItem: IndicationItem) => void;
  onCopyStudy: (prevStudy: Study, currentIndicationId: number) => void;
  onSaveIndicationTitle: (indicationId: number) => Promise<void>;
  isCopying: boolean;
  copyingStudyId: string | number | null;
  // eslint-disable-next-line react/require-default-props
  readOnly?: boolean;
}
/* eslint-enable no-unused-vars */

const IndicationComparisonBlock = ({
  prevItem,
  currItem,
  index,
  indicationApi,
  nctIdAttributeId,
  identifierAttributeId,
  editingStudy,
  onEditStudy,
  onSaveStudy,
  onUpdateStudyField,
  onUpdateStudyAttribute,
  onCopyIndication,
  onCopyStudy,
  onSaveIndicationTitle,
  isCopying,
  copyingStudyId,
  readOnly = false
}: IndicationComparisonBlockProps) => {
  const showCopyIndication = Boolean(
    !readOnly && currItem == null && prevItem != null && prevItem?.indication?.id
  );
  const showCopyStudy = !readOnly && currItem != null && prevItem != null;
  const currIndicationId = currItem?.indication?.id ?? null;
  const apiIndication =
    indicationApi?.indications?.find(
      (item: IndicationItem) => item?.indication?.id === currIndicationId
    ) ?? null;
  const canEditCurrent = Boolean(
    !readOnly && currItem != null && currIndicationId != null && indicationApi && apiIndication
  );

  return (
    <Box sx={styles.root}>
      <Box display='flex' alignItems='center' flexWrap='wrap' gap={1} sx={styles.headerRow}>
        <Typography variant='h6' color='primary' sx={styles.titleTypography}>
          {index + 1}.{' '}
        </Typography>
        {canEditCurrent && apiIndication != null && apiIndication.editOpen ? (
          <>
            <TextField
              placeholder='Indication text'
              size='small'
              sx={styles.textField}
              value={apiIndication.indication?.text ?? ''}
              onChange={e =>
                indicationApi?.updateIndicationText(apiIndication.indication.id, e.target.value)
              }
            />
            <Button
              variant='contained'
              color='primary'
              size='small'
              onClick={() => onSaveIndicationTitle(apiIndication.indication.id)}>
              Save title
            </Button>
          </>
        ) : (
          <>
            <Typography variant='h6' color='primary' sx={styles.titleTypography}>
              {currItem?.indication?.text ??
                prevItem?.indication?.text ??
                apiIndication?.indication?.text ??
                'Indication'}
            </Typography>
            {canEditCurrent && apiIndication != null && (
              <Tooltip title='Edit indication title'>
                <Button
                  size='small'
                  variant='outlined'
                  onClick={() => indicationApi?.editIndication(apiIndication.indication.id)}>
                  Edit title
                </Button>
              </Tooltip>
            )}
          </>
        )}
      </Box>
      <IndicationComparison
        title=''
        parentIndication={prevItem?.indication ?? null}
        currentIndication={currItem?.indication ?? null}
        nctIdAttributeId={nctIdAttributeId}
        identifierAttributeId={identifierAttributeId}
        showActions={showCopyIndication || showCopyStudy}
        onCopyIndication={
          showCopyIndication && prevItem != null ? () => onCopyIndication(prevItem) : undefined
        }
        onCopyStudy={
          showCopyStudy && currIndicationId != null
            ? (prevStudy: Study) => onCopyStudy(prevStudy, currIndicationId)
            : undefined
        }
        isCopying={isCopying}
        copyingStudyId={copyingStudyId}
        editingStudyId={
          canEditCurrent && editingStudy != null && editingStudy.indicationId === currIndicationId
            ? editingStudy.studyId
            : null
        }
        editingStudyData={
          canEditCurrent && editingStudy != null && editingStudy.indicationId === currIndicationId
            ? editingStudy.studyData
            : null
        }
        onEditStudy={
          canEditCurrent && currIndicationId != null
            ? (study: Study) => onEditStudy(currIndicationId, study)
            : undefined
        }
        onSaveStudy={canEditCurrent ? onSaveStudy : undefined}
        onUpdateStudyField={
          canEditCurrent && currIndicationId != null
            ? (key: string, value: string | number | boolean) =>
                onUpdateStudyField(currIndicationId, key, value)
            : undefined
        }
        onUpdateStudyAttribute={
          canEditCurrent && currIndicationId != null
            ? (attrName: string, attrId: number, value: string) =>
                onUpdateStudyAttribute(currIndicationId, attrName, attrId, value)
            : undefined
        }
      />
    </Box>
  );
};

export default IndicationComparisonBlock;
